import { apiRequest } from '@/utils/api-request';
import { useOrgStore } from '@/stores/org-store';
import { DatasetCsvUploadMetadata, QualityAction } from '@/types/source-types';

interface MetadataResolutionPayload {
  column_index: number;
  action: QualityAction;
  new_header?: string;
  fill_value?: string;
}

interface MetadataResolutionResponse {
  metadata: DatasetCsvUploadMetadata | null;
}

interface MetadataResolutionError {
  detail: string;
}

type MetadataResolutionResult =
  | MetadataResolutionResponse
  | MetadataResolutionError;

const extractMetadata = (data: unknown): DatasetCsvUploadMetadata | null => {
  if (!data || typeof data !== 'object') {
    return null;
  }

  if ('metadata' in data) {
    const nested = (data as { metadata?: unknown }).metadata;
    return extractMetadata(nested);
  }

  return data as DatasetCsvUploadMetadata;
};

export const submitMetadataResolutions = async (
  collectionId: string,
  fileKey: string,
  resolutions: MetadataResolutionPayload[],
): Promise<MetadataResolutionResult> => {
  const orgId = useOrgStore.getState().currentOrg?.orgid;

  if (!orgId) {
    throw new Error('Organization ID is required');
  }

  if (!collectionId) {
    throw new Error('Collection ID is required');
  }

  if (!fileKey) {
    throw new Error('Filename is required');
  }

  const baseFilename = fileKey.split('/').pop();

  if (!baseFilename) {
    throw new Error('Filename is required');
  }

  const response = await apiRequest<
    DatasetCsvUploadMetadata | { metadata?: DatasetCsvUploadMetadata }
  >({
    url: `/uploads/${orgId}/${collectionId}/${encodeURIComponent(
      baseFilename,
    )}/metadata-resolutions`,
    method: 'POST',
    target: 'platform',
    body: {
      resolutions,
    },
  });

  if (response?.detail) {
    return { detail: response.detail };
  }

  const metadata = extractMetadata(response?.data ?? null);
  return { metadata: metadata ?? null };
};

export type { MetadataResolutionPayload };
